import {
  IS_MEDIA_OSS_ENABLED,
  MEDIA_OSS_DOCUMENTS_PATH_PREFIX,
  MEDIA_OSS_PHOTOS_PATH_PREFIX,
} from '../config';
import { parseMediaUrl } from '../api/gramjs/helpers/mediaUrlParser';
import { IS_IOS, IS_OPUS_SUPPORTED, IS_SAFARI } from './browser/windowEnvironment';

const SIZE_ALIASES: Record<string, string> = {
  a: 's',
  b: 'm',
  c: 'm',
  u: 'o',
  w: 'y',
};

const AVATAR_OSS_SIZE = 'a';
const PROFILE_OSS_SIZE = 'c';
const AVATAR_OSS_PHOTO_ID_REGEX = /^\d+$/;

function normalizeSizeType(sizeType: string) {
  return SIZE_ALIASES[sizeType] || sizeType;
}

export function isAvatarOrProfileMediaHash(mediaHash: string | undefined | false): boolean {
  return Boolean(mediaHash && (mediaHash.startsWith('avatar') || mediaHash.startsWith('profile')));
}

export const OSS_UNAVAILABLE_TTL_MS = 30000;

const unavailableOssPaths = new Map<string, number>();

function getOssPathKey(ossUrl: string) {
  const queryIndex = ossUrl.indexOf('?');
  return queryIndex === -1 ? ossUrl : ossUrl.slice(0, queryIndex);
}

function isKeyUnavailable(key: string) {
  const markedAt = unavailableOssPaths.get(key);
  if (markedAt === undefined) {
    return false;
  }

  if (Date.now() - markedAt >= OSS_UNAVAILABLE_TTL_MS) {
    unavailableOssPaths.delete(key);
    return false;
  }

  return true;
}

export function markOssMediaUnavailable(ossUrl: string) {
  unavailableOssPaths.set(getOssPathKey(ossUrl), Date.now());
}

export function isOssMediaUnavailable(ossUrl: string) {
  return isKeyUnavailable(getOssPathKey(ossUrl));
}

export function clearOssMediaUnavailable(ossUrl: string) {
  return unavailableOssPaths.delete(getOssPathKey(ossUrl));
}

export function getOssUnavailableCount() {
  [...unavailableOssPaths.keys()].forEach(isKeyUnavailable);

  return unavailableOssPaths.size;
}

export function resetOssUnavailable() {
  unavailableOssPaths.clear();
}

export function isOssPhotoUrl(ossUrl: string | undefined): boolean {
  return Boolean(ossUrl?.startsWith(`${MEDIA_OSS_PHOTOS_PATH_PREFIX}/`));
}

export function isOssMediaPath(ossUrl: string | undefined): boolean {
  return Boolean(
    ossUrl?.startsWith(`${MEDIA_OSS_PHOTOS_PATH_PREFIX}/`)
    || ossUrl?.startsWith(`${MEDIA_OSS_DOCUMENTS_PATH_PREFIX}/`),
  );
}

export function isOssPhotoMediaHash(mediaHash: string | undefined | false): boolean {
  return Boolean(mediaHash && isOssPhotoUrl(buildOssMediaUrl(mediaHash)));
}

export function buildOssMediaUrl(mediaHash: string): string | undefined {
  const url = buildOssMediaPath(mediaHash);
  if (!url || isKeyUnavailable(getOssPathKey(url))) {
    return undefined;
  }

  return url;
}

export function buildOssMediaPathUnchecked(mediaHash: string): string | undefined {
  return buildOssMediaPath(mediaHash);
}

function buildOssAvatarPath(mediaHash: string): string | undefined {
  const queryIndex = mediaHash.indexOf('?');
  if (queryIndex === -1) {
    return undefined;
  }

  const photoId = mediaHash.slice(queryIndex + 1);
  if (!AVATAR_OSS_PHOTO_ID_REGEX.test(photoId)) {
    return undefined;
  }

  const size = mediaHash.startsWith('profile') ? PROFILE_OSS_SIZE : AVATAR_OSS_SIZE;
  return `${MEDIA_OSS_PHOTOS_PATH_PREFIX}/${size}/${photoId}.dat`;
}

function buildOssMediaPath(mediaHash: string): string | undefined {
  if (!IS_MEDIA_OSS_ENABLED) {
    return undefined;
  }

  if (isAvatarOrProfileMediaHash(mediaHash)) {
    return buildOssAvatarPath(mediaHash);
  }

  const parsed = parseMediaUrl(mediaHash);
  if (!parsed) {
    return undefined;
  }

  const { entityType, entityId } = parsed;
  const sizeType = parsed.sizeType || extractSizeFromQuery(mediaHash);

  if (entityType === 'photo') {
    if (!sizeType) {
      return undefined;
    }

    const size = normalizeSizeType(sizeType);
    return `${MEDIA_OSS_PHOTOS_PATH_PREFIX}/${size}/${entityId}.dat`;
  }

  if (entityType === 'document') {
    if (sizeType) {
      return undefined;
    }

    const query = buildOssDocumentQuery(mediaHash);
    return `${MEDIA_OSS_DOCUMENTS_PATH_PREFIX}/${entityId}.dat${query}`;
  }

  return undefined;
}

function extractSizeFromQuery(mediaHash: string) {
  const queryIndex = mediaHash.indexOf('?');
  if (queryIndex === -1) {
    return undefined;
  }

  const params = new URLSearchParams(mediaHash.slice(queryIndex + 1));
  return params.get('size') || undefined;
}

const CLIENT_ONLY_QUERY_PARAMS = new Set(['voice']);

function buildOssDocumentQuery(mediaHash: string) {
  const queryIndex = mediaHash.indexOf('?');
  if (queryIndex === -1) {
    return '';
  }

  const params = new URLSearchParams(mediaHash.slice(queryIndex + 1));
  CLIENT_ONLY_QUERY_PARAMS.forEach((key) => params.delete(key));

  const query = params.toString();
  return query ? `?${query}` : '';
}

function isOssVoiceInlineHash(mediaHash: string | undefined | false): boolean {
  if (!mediaHash) {
    return false;
  }

  const queryIndex = mediaHash.indexOf('?');
  if (queryIndex === -1) {
    return false;
  }

  return new URLSearchParams(mediaHash.slice(queryIndex + 1)).has('voice');
}

export function needsOssVoiceBlobFetch(mediaHash: string | undefined | false): boolean {
  return IS_MEDIA_OSS_ENABLED
    && isOssVoiceInlineHash(mediaHash)
    && (IS_IOS || IS_SAFARI || !IS_OPUS_SUPPORTED);
}

export function stripClientMediaHashParams(mediaHash: string) {
  const queryIndex = mediaHash.indexOf('?');
  if (queryIndex === -1) {
    return mediaHash;
  }

  const base = mediaHash.slice(0, queryIndex);
  const params = new URLSearchParams(mediaHash.slice(queryIndex + 1));
  CLIENT_ONLY_QUERY_PARAMS.forEach((key) => params.delete(key));

  const query = params.toString();
  return query ? `${base}?${query}` : base;
}

export function isOssServingUrl(prepared: string | undefined): boolean {
  if (!prepared || !IS_MEDIA_OSS_ENABLED) {
    return false;
  }

  return prepared.includes(`${MEDIA_OSS_PHOTOS_PATH_PREFIX}/`)
    || prepared.includes(`${MEDIA_OSS_DOCUMENTS_PATH_PREFIX}/`);
}

export function isOssMediaHash(mediaHash: string | undefined | false): boolean {
  return Boolean(mediaHash && buildOssMediaUrl(mediaHash));
}

export function isImagePreparedUrl(prepared: string | undefined): boolean {
  if (!prepared) {
    return false;
  }

  if (prepared.startsWith('data:') || prepared.startsWith('blob:')) {
    return true;
  }

  if (!isOssServingUrl(prepared)) {
    return true;
  }

  return prepared.includes(`${MEDIA_OSS_PHOTOS_PATH_PREFIX}/`);
}
